import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  X,
  Shield,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Database,
  RefreshCw,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { adminAuthService } from '../../../shared/services/adminAuth'
import { adminBackupApi } from '../../../shared/services/adminBackupApi'

interface BackupFile {
  id: string
  filename: string
  size: number
  sizeFormatted: string
  createdAt: string
  type: 'hourly' | 'manual' | 'daily' | 'weekly' | 'monthly' | 'pre-restore'
  status: 'success' | 'failed' | 'in_progress'
}

interface RestoreStep {
  id: number
  title: string
  description: string
  completed: boolean
  active: boolean
}

interface RestoreWizardModalProps {
  isOpen: boolean
  onClose: () => void
  selectedBackup: BackupFile | null
  onRestoreComplete: () => void
}

const RestoreWizardModal: React.FC<RestoreWizardModalProps> = ({
  isOpen,
  onClose,
  selectedBackup,
  onRestoreComplete,
}) => {
  const [currentStep, setCurrentStep] = useState(1)
  const [restoreOptions, setRestoreOptions] = useState({
    createPreBackup: true,
    verifyAfterRestore: true,
    restoreType: 'full' as 'full' | 'selective',
  })
  const [confirmationText, setConfirmationText] = useState('')

  const steps: RestoreStep[] = [
    {
      id: 1,
      title: '백업 선택',
      description: '복원할 백업 파일을 확인하세요',
      completed: currentStep > 1,
      active: currentStep === 1,
    },
    {
      id: 2,
      title: '복원 옵션',
      description: '복원 방식과 안전 옵션을 설정하세요',
      completed: currentStep > 2,
      active: currentStep === 2,
    },
    {
      id: 3,
      title: '안전성 확인',
      description: '복원의 위험성을 이해하고 확인하세요',
      completed: currentStep > 3,
      active: currentStep === 3,
    },
  ]

  if (!isOpen || !selectedBackup) {
    return null
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white shadow-xl dark:bg-gray-800"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
            <div className="flex items-center">
              <Shield className="mr-3 h-6 w-6 text-blue-600" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  안전한 백업 복원
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  단계별 안전 절차를 통한 데이터베이스 복원
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${
                      step.completed
                        ? 'border-green-500 bg-green-100 text-green-600'
                        : step.active
                          ? 'border-blue-500 bg-blue-100 text-blue-600'
                          : 'border-gray-300 bg-gray-100 text-gray-400'
                    }`}
                  >
                    {step.completed ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <span className="text-sm font-medium">{step.id}</span>
                    )}
                  </div>
                  <div className="ml-3 hidden sm:block">
                    <p
                      className={`text-sm font-medium ${
                        step.active ? 'text-blue-600' : 'text-gray-500'
                      }`}
                    >
                      {step.title}
                    </p>
                    <p className="text-xs text-gray-400">{step.description}</p>
                  </div>
                  {index < steps.length - 1 && (
                    <ArrowRight className="mx-4 h-4 w-4 text-gray-300" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="p-6">
            {/* Step 1: 백업 선택 */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                    선택된 백업 파일 확인
                  </h3>
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-700">
                    <div className="flex items-center">
                      <Database className="mr-4 h-8 w-8 text-blue-600" />
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {selectedBackup.filename}
                        </h4>
                        <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          <span>크기: {selectedBackup.sizeFormatted}</span>
                          <span className="mx-2">•</span>
                          <span>
                            생성일:{' '}
                            {new Date(
                              selectedBackup.createdAt
                            ).toLocaleString()}
                          </span>
                          <span className="mx-2">•</span>
                          <span>유형: {selectedBackup.type}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
                  <div className="flex">
                    <AlertTriangle className="mr-3 mt-0.5 h-5 w-5 text-yellow-400" />
                    <div>
                      <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                        중요한 안내사항
                      </h4>
                      <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                        <ul className="list-disc space-y-1 pl-5">
                          <li>
                            복원 과정에서{' '}
                            <strong>
                              현재 모든 데이터가 선택한 백업으로 교체
                            </strong>
                            됩니다
                          </li>
                          <li>
                            복원 후에는 백업 생성 이후의 모든 변경사항이{' '}
                            <strong>영구적으로 손실</strong>됩니다
                          </li>
                          <li>
                            복원 과정은 <strong>되돌릴 수 없으며</strong>, 완료
                            후 취소할 수 없습니다
                          </li>
                          <li>
                            안전을 위해 복원 전 현재 데이터의{' '}
                            <strong>자동 백업을 강력히 권장</strong>합니다
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: 복원 옵션 */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                    복원 옵션 설정
                  </h3>

                  <div className="space-y-4">
                    {/* 복원 전 백업 생성 */}
                    <div className="flex items-start">
                      <div className="flex h-5 items-center">
                        <input
                          id="createPreBackup"
                          type="checkbox"
                          checked={restoreOptions.createPreBackup}
                          onChange={e =>
                            setRestoreOptions({
                              ...restoreOptions,
                              createPreBackup: e.target.checked,
                            })
                          }
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </div>
                      <div className="ml-3">
                        <label
                          htmlFor="createPreBackup"
                          className="text-sm font-medium text-gray-900 dark:text-white"
                        >
                          복원 전 현재 데이터 백업 생성 (강력 권장)
                        </label>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          복원 실패 시 현재 상태로 되돌릴 수 있도록 안전 백업을
                          생성합니다
                        </p>
                      </div>
                    </div>

                    {/* 복원 후 검증 */}
                    <div className="flex items-start">
                      <div className="flex h-5 items-center">
                        <input
                          id="verifyAfterRestore"
                          type="checkbox"
                          checked={restoreOptions.verifyAfterRestore}
                          onChange={e =>
                            setRestoreOptions({
                              ...restoreOptions,
                              verifyAfterRestore: e.target.checked,
                            })
                          }
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </div>
                      <div className="ml-3">
                        <label
                          htmlFor="verifyAfterRestore"
                          className="text-sm font-medium text-gray-900 dark:text-white"
                        >
                          복원 후 데이터 무결성 검증
                        </label>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          복원 완료 후 데이터베이스 상태를 검증하여 정상적으로
                          복원되었는지 확인합니다
                        </p>
                      </div>
                    </div>
                  </div>

                  {!restoreOptions.createPreBackup && (
                    <div className="rounded-md border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                      <div className="flex">
                        <AlertTriangle className="mr-3 h-5 w-5 text-red-400" />
                        <div>
                          <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                            위험: 복원 전 백업 미설정
                          </h4>
                          <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                            복원 전 백업을 생성하지 않으면 복원 실패 시 현재
                            데이터를 복구할 수 없습니다. 안전을 위해 반드시
                            체크해주세요.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: 안전성 확인 */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h3 className="mb-4 text-lg font-medium text-gray-900 dark:text-white">
                    복원 위험성 이해 및 최종 확인
                  </h3>

                  <div className="rounded-md border border-red-200 bg-red-50 p-6 dark:border-red-800 dark:bg-red-900/20">
                    <div className="flex">
                      <AlertTriangle className="mr-4 mt-1 h-6 w-6 text-red-500" />
                      <div className="flex-1">
                        <h4 className="mb-3 text-lg font-medium text-red-800 dark:text-red-200">
                          ⚠️ 데이터베이스 복원 위험 고지
                        </h4>
                        <div className="space-y-2 text-sm text-red-700 dark:text-red-300">
                          <p>
                            <strong>복원 시 영향받는 데이터:</strong>
                          </p>
                          <ul className="list-disc space-y-1 pl-5">
                            <li>모든 사용자 계정 및 인증 정보</li>
                            <li>모든 단축 URL 및 클릭 통계</li>
                            <li>폴더, 태그 등 모든 북마크 데이터</li>
                            <li>보안 설정 및 로그</li>
                            <li>결제 내역 및 구독 정보</li>
                          </ul>
                          <p className="mt-3 font-semibold">
                            위 모든 데이터가 선택한 백업 시점의 상태로
                            되돌아갑니다.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-md border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                    <h4 className="mb-2 text-sm font-medium text-blue-800 dark:text-blue-200">
                      복원 계획 요약
                    </h4>
                    <div className="text-sm text-blue-700 dark:text-blue-300">
                      <p>
                        <strong>백업 파일:</strong> {selectedBackup.filename}
                      </p>
                      <p>
                        <strong>백업 생성일:</strong>{' '}
                        {new Date(selectedBackup.createdAt).toLocaleString()}
                      </p>
                      <p>
                        <strong>복원 전 백업:</strong>{' '}
                        {restoreOptions.createPreBackup
                          ? '생성함'
                          : '생성 안함'}
                      </p>
                      <p>
                        <strong>복원 후 검증:</strong>{' '}
                        {restoreOptions.verifyAfterRestore
                          ? '수행함'
                          : '수행 안함'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="confirmation"
                      className="mb-2 block text-sm font-medium text-gray-900 dark:text-white"
                    >
                      위험성을 이해했음을 확인하기 위해 다음 텍스트를 정확히
                      입력하세요:
                    </label>
                    <div className="mb-3 rounded bg-gray-100 p-3 dark:bg-gray-700">
                      <code className="font-mono text-sm text-gray-900 dark:text-white">
                        복원 위험성을 이해하고 진행합니다
                      </code>
                    </div>
                    <input
                      id="confirmation"
                      type="text"
                      value={confirmationText}
                      onChange={e => setConfirmationText(e.target.value)}
                      placeholder="위 텍스트를 정확히 입력하세요"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="mt-8 flex justify-between border-t border-gray-200 pt-6 dark:border-gray-700">
              <button
                onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                disabled={currentStep === 1}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                이전
              </button>

              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  취소
                </button>

                {currentStep < 3 ? (
                  <button
                    onClick={() => setCurrentStep(Math.min(3, currentStep + 1))}
                    className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                  >
                    다음
                  </button>
                ) : (
                  <RestoreExecuteButton
                    disabled={
                      confirmationText !== '복원 위험성을 이해하고 진행합니다'
                    }
                    selectedBackup={selectedBackup}
                    restoreOptions={restoreOptions}
                    onSuccess={() => {
                      onRestoreComplete()
                      onClose()
                    }}
                    onError={(error: string) => {
                      toast.error(error)
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

/**
 * 복원 실행 버튼 컴포넌트
 */
interface RestoreExecuteButtonProps {
  disabled: boolean
  selectedBackup: BackupFile
  restoreOptions: any
  onSuccess: () => void
  onError: (error: string) => void
}

const RestoreExecuteButton: React.FC<RestoreExecuteButtonProps> = ({
  disabled,
  selectedBackup,
  restoreOptions,
  onSuccess,
  onError,
}) => {
  const [isRestoring, setIsRestoring] = useState(false)

  const executeRestore = async () => {
    if (disabled) {
      onError('확인 텍스트가 정확하지 않습니다')
      return
    }

    setIsRestoring(true)

    try {
      const token = adminAuthService.getToken()
      if (!token) {
        throw new Error('관리자 인증이 필요합니다')
      }

      // 복원 진행률은 서버가 알려주지 않는다. 예전에는 Math.random 으로
      // 막대를 채워 실제 복원과 무관한 숫자를 보여줬다. 진행 단계를 모르면
      // 모른다고 표시한다 — 아래 막대는 불확정 표시다.
      // 실패는 서비스가 서버 메시지와 함께 예외로 올린다
      const result = await adminBackupApi.restore(
        selectedBackup.filename,
        restoreOptions
      )

      toast.success('백업 복원이 성공적으로 완료되었습니다!', {
        duration: 5000,
        icon: '✅',
      })

      // 복원 결과 정보 표시
      if (result.preBackupCreated) {
        toast.success(`안전 백업 생성됨: ${result.preBackupCreated}`, {
          duration: 4000,
        })
      }

      if (result.verification?.success) {
        toast.success(
          `데이터 검증 완료 (점수: ${result.verification.score}%)`,
          {
            duration: 4000,
          }
        )
      }

      onSuccess()
    } catch (error: any) {
      console.error('복원 실행 실패:', error)

      let errorMessage = '복원 중 오류가 발생했습니다'
      if (error.message) {
        errorMessage = error.message
      }

      toast.error(errorMessage, {
        duration: 8000,
        icon: '❌',
      })

      onError(errorMessage)
    } finally {
      setIsRestoring(false)
    }
  }

  return (
    <div className="relative">
      <button
        onClick={executeRestore}
        disabled={disabled || isRestoring}
        className="flex items-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isRestoring ? (
          <>
            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            복원 중...
          </>
        ) : (
          '복원 실행'
        )}
      </button>

      {/* 진행 표시 — 서버가 진행률을 주지 않으므로 불확정 막대를 쓴다 */}
      {isRestoring && (
        <div className="absolute left-0 right-0 top-full mt-2">
          <div
            role="progressbar"
            aria-label="데이터베이스 복원 중"
            className="h-2 overflow-hidden rounded-full bg-gray-200"
          >
            <div className="h-2 w-1/3 animate-indeterminate rounded-full bg-red-500" />
          </div>
          <p className="mt-1 text-center text-xs text-gray-500">
            데이터베이스 복원 중... 완료까지 걸리는 시간은 알 수 없습니다
          </p>
        </div>
      )}
    </div>
  )
}

export default RestoreWizardModal
